import { action, computed, makeObservable, observable, ObservableMap } from 'mobx';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { IPlayerAdapter } from '../player/playerSchema';
import type { MediaPlaylist as HlsAudioTrack } from 'hls.js';
import { PlayerAdapterState } from './playerAdapterState';

type Props = BindingProps<{
  adapter: IPlayerAdapter
}>

enum AudioTracksStatus {
  Active = 'active',
  Switching = 'switching',
  // Loading = 'loading',
  // Loaded = 'loaded',
  Error = 'error'
}

export class PlayerAudioTracksState
  extends StoreNode {

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'setActiveTrack' | 'replaceTracks'>(this, {
      adapter: computed,
      audioTracks: computed,
      hasMultipleAudioTracks: computed,
      activeAudioTrackId: observable,
      selectedAudioTrackId: observable,
      trackStatus: observable,
      activeAudioTrack: computed,
      selectedAudioTrack: computed,
      isTrackActive: computed,
      setTrackStatus: action,
      setSwitching: action,
      setActive: action,
      setError: action,
      refreshStatus: action,
      switchTrack: action,
      setActiveTrack: action,
      setSelectedTrack: action,
      replaceTracks: action,
      refreshAudioTracks: action,
      refreshActiveAudioTrack: action,
      reset: action,
    });
  }

  get adapter(): PlayerAdapterState {
    return this.getResolvedProp('adapter');
  }

  readonly audioTracksMap: ObservableMap<string, HlsAudioTrack> =
    observable.map<string, HlsAudioTrack>();

  get audioTracks(): HlsAudioTrack[] {
    return [...this.audioTracksMap.values()];
  }

  get hasMultipleAudioTracks(): boolean {
    return this.audioTracks.length > 1;
  }

  activeAudioTrackId: string | null = null; // hls active

  selectedAudioTrackId: string | null = null; // ui selected

  trackStatus: AudioTracksStatus | null = null;

  get activeAudioTrack(): HlsAudioTrack | null {
    if (this.audioTracks.length === 0 || !this.activeAudioTrackId)
      return null;

    return this.getTrack(this.activeAudioTrackId) ?? null;
  }

  get selectedAudioTrack(): HlsAudioTrack | null {
    if (this.audioTracks.length === 0 || !this.selectedAudioTrackId)
      return null;

    return this.getTrack(this.selectedAudioTrackId) ?? null;
  }

  get isTrackActive(): boolean {
    return this.trackStatus === AudioTracksStatus.Active;
  }

  // non-observable properties section
  get activeHlsAudioTrackId(): number | null {
    return this.adapter.hls?.audioTrack ?? null;
  }
  get activeHlsAudioTracks(): HlsAudioTrack[] {
    return this.adapter.hls?.audioTracks ?? [];
  }
  // end section

  setTrackStatus(status: AudioTracksStatus | null) {
    this.trackStatus = status;
  }

  setSwitching() {
    this.setTrackStatus(AudioTracksStatus.Switching);
  }

  setActive() {
    this.setTrackStatus(AudioTracksStatus.Active);
  }

  setError() {
    this.setTrackStatus(AudioTracksStatus.Error);
  }

  refreshStatus() {
    this.setTrackStatus(null);
  }

  switchTrack = (track: HlsAudioTrack) => {
    const trackId = track.id?.toString() ?? null;
    if (!this.isTrackActive || this.selectedAudioTrackId === trackId) // if the audio track is still loading the action will be canceled to avoid potential issues 
      return;

    this.adapter.setHlsAudioTrack(track);
    this.setSelectedTrack(trackId);
  }

  getTrack(id: string) {
    return this.audioTracks.find(track => track.id.toString() === id);
  }

  private setActiveTrack(id: string | null) {
    this.activeAudioTrackId = id;
    this.selectedAudioTrackId = id;
    if (!id) {
      this.refreshStatus();
      return;
    }
    this.setActive();
  }

  setSelectedTrack(id: string | null) {
    this.selectedAudioTrackId = id;
    if (!id) {
      this.refreshStatus();
      return;
    }
    this.setSwitching();
  }

  private replaceTracks(tracks: HlsAudioTrack[]) {
    this.audioTracksMap.replace(tracks.map(track => [String(track.id), track]));
  }

  refreshAudioTracks() {
    this.replaceTracks(this.activeHlsAudioTracks);
  }

  refreshActiveAudioTrack() {
    this.setActiveTrack(this.activeHlsAudioTrackId?.toString() ?? null);
  }

  reset() {
    // audio tracks
    this.audioTracksMap.clear();
    this.activeAudioTrackId = null;
    this.selectedAudioTrackId = null;
    this.trackStatus = null;
  }

}
